import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Circle, FileText, Mic, StopCircle, Loader2, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useLocation } from '@/contexts/LocationContext';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import AuthService from '@/lib/auth';
import biometricAuthService from '@/services/biometricAuthService';
import { convertBlobToWav } from '@/utils/audioConverter';
import MainLayout from '@/layouts/MainLayout';

interface QuestionOption {
  text: string;
  score: number;
  value?: number;
  label?: string;
  additional_input?: boolean;
}

interface BaseQuestion {
  id: string;
  question: string;
  options: QuestionOption[];
  selected?: number | null;
  comment?: string;
  audio_file_url?: string;
  original_detected_language?: string;
  original_language_comment_transcribed?: string;
}

interface Question extends BaseQuestion { }

interface QuestionWithSelected extends BaseQuestion {
  selectedOption: number;
}

interface QuestionnaireTemplate {
  id: string;
  name: string;
  description?: string;
  source_citations?: string | null;
  questions: Question[];
}

interface TestProgress {
  templateId: string;
  name: string;
  questions: Question[];
  completed: boolean;
  totalScore?: number;
  source_citations?: string | null;
}

const Questionnaire: React.FC = () => {
  const navigate = useNavigate();
  const { patientId = '', encounterId: _encounterId = '', type = '' } = useParams<{ patientId: string; encounterId: string; type: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<'selection' | 'questionnaire' | 'review' | 'complete'>('selection');
  const [availableTests, setAvailableTests] = useState<QuestionnaireTemplate[]>([]);
  const [testsProgress, setTestsProgress] = useState<TestProgress[]>([]);
  const [currentTestIndex, setCurrentTestIndex] = useState(0);
  const [allTestsCompleted, setAllTestsCompleted] = useState(false);
  const [isLoadingTests, setIsLoadingTests] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientName, setPatientName] = useState<string>('');
  const [showWelcomeModal, setShowWelcomeModal] = useState(true);

  // Audio recording states
  const [currentQuestionForComment, setCurrentQuestionForComment] = useState<QuestionWithSelected | null>(null);
  const [commentText, setCommentText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [modalClosedManually, setModalClosedManually] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [recordingTimeLeft, setRecordingTimeLeft] = useState(0);
  const [transcription, setTranscription] = useState<{
    text: string;
    segments?: Array<{ start: number; end: number; text: string }>;
    language?: string;
    originalLanguage?: string;
    originalText?: string;
    audioFileUrl?: string;
    s3Key?: string;
  } | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { selectedLocationId, locations } = useLocation();
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load patient's selected tests and questionnaire templates
  useEffect(() => {
    const loadPatientTests = async () => {
      if (!patientId) {
        setError('Patient ID is required');
        return;
      }

      setIsLoadingTests(true);
      setError(null);

      try {
        // Get patient details and selected tests using AuthService
        const patient = await AuthService.getPatient(patientId);

        if (!patient) {
          throw new Error('Patient not found');
        }

        // Set patient name
        const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
        setPatientName(fullName || 'Patient');

        // Parse selected tests
        let selectedTestIds: string[] = [];
        if (patient.selected_tests) {
          try {
            const parsed = Array.isArray(patient.selected_tests)
              ? patient.selected_tests
              : JSON.parse(patient.selected_tests as string);
              
            if (type && type !== 'all' && parsed.includes(type)) {
              selectedTestIds = [type];
            } else {
              selectedTestIds = parsed;
            }
          } catch (e) {
            console.error('Error parsing selected tests:', e);
            throw new Error('Error parsing assigned tests');
          }
        }

        if (selectedTestIds.length === 0) {
          throw new Error('No tests assigned to this patient');
        }

        // Debug: Log the selected test IDs
        console.log('Selected test IDs:', selectedTestIds);

        // Fetch questionnaire templates using AuthService
        const templates = await AuthService.getQuestionnaireTemplates({ ids: selectedTestIds });
        const templatesArray = Array.isArray(templates) ? templates : ((templates as any)?.data || []);

        console.log('Fetched templates:', templatesArray);

        if (templatesArray.length === 0) {
          throw new Error('No valid questionnaires found for assigned tests');
        }

        // Process templates and questions
        const processedTemplates: QuestionnaireTemplate[] = templatesArray.map((template: any) => {
          let questionsData: Question[] = [];

          if (template.questions) {
            try {
              questionsData = Array.isArray(template.questions)
                ? template.questions
                : JSON.parse(template.questions as unknown as string);
            } catch (e) {
              console.error(`Error parsing questions for template ${template.id}:`, e);
              questionsData = [];
            }
          }

          // Process questions to ensure correct structure
          const processedQuestions = questionsData.map((q: Question, qIndex: number) => ({
            ...q,
            id: q.id || `question_${qIndex}`,
            selected: null,
            options: q.options.map((opt, idx) => ({
              ...opt,
              value: opt.value ?? idx,
              label: opt.label || opt.text || `Option ${idx + 1}`
            }))
          }));

          // Generate name and description from ID since they don't exist in the table
          const generateNameFromId = (id: string): string => {
            if (id.toLowerCase().includes('phq')) return 'PHQ-9 Depression Screening';
            if (id.toLowerCase().includes('gad')) return 'GAD-7 Anxiety Screening';
            if (id.toLowerCase().includes('depression')) return 'Depression Assessment';
            if (id.toLowerCase().includes('anxiety')) return 'Anxiety Assessment';
            // Default: capitalize and format the ID
            return id.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          };

          const generateDescriptionFromId = (id: string): string => {
            if (id.toLowerCase().includes('phq')) return 'Patient Health Questionnaire for depression screening';
            if (id.toLowerCase().includes('gad')) return 'Generalized Anxiety Disorder assessment';
            return `${generateNameFromId(id)} questionnaire`;
          };

          return {
            id: template.id,
            name: template.full_name || template.name || generateNameFromId(template.id),
            description: template.description || generateDescriptionFromId(template.id),
            source_citations: template.source_citations,
            questions: processedQuestions
          };
        }).filter((template: any) => template.questions.length > 0);

        if (processedTemplates.length === 0) {
          throw new Error('No valid questions found in assigned tests');
        }

        setAvailableTests(processedTemplates);

        // Initialize progress for each test
        const initialProgress: TestProgress[] = processedTemplates.map((template: any) => ({
          templateId: template.id,
          name: template.name,
          source_citations: template.source_citations,
          questions: template.questions.map((q: any) => ({ ...q, selected: null })),
          completed: false
        }));

        setTestsProgress(initialProgress);

        // If only one test, go directly to questionnaire
        if (processedTemplates.length === 1) {
          setCurrentStep('questionnaire');
        }

      } catch (err: any) {
        console.error('Error loading patient tests:', err);
        setError(err.message || 'Failed to load questionnaires');
        toast.error(err.message || 'Failed to load questionnaires');
      } finally {
        setIsLoadingTests(false);
      }
    };

    loadPatientTests();
  }, [patientId]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);

  const getCurrentTest = () => {
    return testsProgress[currentTestIndex] || null;
  };

  const canContinueCurrentTest = () => {
    const currentTest = getCurrentTest();
    if (!currentTest) return false;
    return currentTest.questions.every((q) => q.selected !== null && q.selected !== undefined);
  };

  const handleResponse = (questionId: string, optionValue: number) => {
    setTestsProgress(prev => {
      const updated = [...prev];
      if (updated[currentTestIndex]) {
        updated[currentTestIndex].questions = updated[currentTestIndex].questions.map((question: any) => {
          if (question.id === questionId) {
            const updatedQuestion = { ...question, selected: optionValue };
            
            // Find the selected option to check for additional_input
            const selectedOption = question.options.find((opt: any) => opt.value === optionValue);

            if (selectedOption?.additional_input) {
              setCurrentQuestionForComment({
                ...updatedQuestion,
                selectedOption: optionValue
              });
              setCommentText(updatedQuestion.comment || '');
            } else {
              // Clear comment if a non-additional-input option is selected
              updatedQuestion.comment = '';
            }

            return updatedQuestion;
          }
          return question;
        });
      }
      return updated;
    });
  };

  const calculateScore = (testQuestions: Question[]): number => {
    return testQuestions.reduce((sum, question) => {
      const selectedOption = question.selected !== undefined && question.selected !== null
        ? question.options.find(opt => opt.value === question.selected)
        : null;
      return sum + (selectedOption?.score || 0);
    }, 0);
  };

  const completeCurrentTest = () => {
    const currentTest = getCurrentTest();
    if (!currentTest) return;

    const totalScore = calculateScore(currentTest.questions);

    setTestsProgress(prev => {
      const updated = [...prev];
      if (updated[currentTestIndex]) {
        updated[currentTestIndex].completed = true;
        updated[currentTestIndex].totalScore = totalScore;
      }
      return updated;
    });

    // Check if this was the last test
    if (currentTestIndex >= testsProgress.length - 1) {
      setAllTestsCompleted(true);
      setCurrentStep('review');
    } else {
      // Move to next test
      setCurrentTestIndex(prev => prev + 1);
    }
  };

  const saveComment = () => {
    if (!currentQuestionForComment) return;

    setTestsProgress(prev => {
      const updated = [...prev];
      if (updated[currentTestIndex]) {
        updated[currentTestIndex].questions = updated[currentTestIndex].questions.map((question: any) =>
          question.id === currentQuestionForComment.id
            ? {
              ...question,
              comment: commentText,
              selected: currentQuestionForComment.selectedOption,
              ...(transcription && {
                audio_file_url: transcription.s3Key || transcription.audioFileUrl,
                original_detected_language: transcription.originalLanguage,
                original_language_comment_transcribed: transcription.originalText
              })
            }
            : question
        );
      }
      return updated;
    });

    setCurrentQuestionForComment(null);
    setCommentText('');
    setTranscription(null);
  };

  const closeCommentModal = () => {
    if (isRecording && mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      setModalClosedManually(true);
      mediaRecorderRef.current.stop();
      setModalClosedManually(false);
      setIsRecording(false);
      setRecordingTimeLeft(0);
    }

    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      mediaStreamRef.current = null;
    }

    if (currentQuestionForComment) {
      setTestsProgress(prev => {
        const updated = [...prev];
        if (updated[currentTestIndex]) {
          updated[currentTestIndex].questions = updated[currentTestIndex].questions.map(question =>
            question.id === currentQuestionForComment.id
              ? { ...question, selected: null, comment: '' }
              : question
          );
        }
        return updated;
      });
    }

    setCurrentQuestionForComment(null);
    setCommentText('');
    setTranscription(null);
    setIsProcessingAudio(false);
    setRecordingTimeLeft(0);
  };

  const handleFormSubmit = async () => {
    if (!patientId) {
      toast.error('Missing patient information');
      return;
    }

    setIsLoading(true);
    try {
      // Submit all completed questionnaires
      const submissions = testsProgress.map((test: any) => ({
        patient_id: patientId,
        // Use the canonical type: questionnaire_templates.id == questionnaire_types.name
        questionnaire_type: test.templateId,
        answers: JSON.stringify(test.questions),
        total_score: test.totalScore || 0,
        self_reported: true,
        status: 'pending for review'
      }));

      await AuthService.submitQuestionnaireResponse(submissions);
      setCurrentStep('complete');
      toast.success('All questionnaires submitted successfully!');
    } catch (error: any) {
      console.error('Error submitting questionnaires:', error);
      toast.error('Error saving questionnaire responses. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      // Explicitly request audio/webm — browser default is webm, not wav
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';

      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      const recordedMime = mediaRecorder.mimeType || 'audio/webm';
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsProcessingAudio(true);

        try {
          if (modalClosedManually) {
            return;
          }

          const rawBlob = new Blob(audioChunksRef.current, { type: recordedMime });

          // Server only supports .aac, .mp3, .ogg, .wav, .flac, .m4a
          // Browser records in WebM — convert to WAV before sending
          let audioFile: File;
          try {
            const wavBlob = await convertBlobToWav(rawBlob);
            audioFile = new File([wavBlob], `recording_${Date.now()}.wav`, { type: 'audio/wav' });
            console.log('Converted audio to WAV:', audioFile.size, 'bytes');
          } catch (convErr) {
            console.warn('WAV conversion failed, sending raw blob:', convErr);
            const ext = recordedMime.includes('webm') ? 'webm' : recordedMime.includes('mp4') ? 'mp4' : 'wav';
            audioFile = new File([rawBlob], `recording_${Date.now()}.${ext}`, { type: recordedMime });
          }

          const locationObj = locations.find(l => l.value === selectedLocationId);
          const locationLabel = locationObj ? locationObj.label : "Unknown — Unknown";
          const labelParts = locationLabel.split(" — ");
          // label is formatted as `${hospitalName} — ${branchName}`
          const parsedHospitalName = labelParts[0] || locationObj?.hospitalName || "";
          const parsedBranchName = labelParts[1] || locationObj?.branchName || "";

          // Transcribe the audio
          const response = await biometricAuthService.transcribeAudio({
            audioFile,
            branch: parsedBranchName,
            hospital_name: parsedHospitalName,
            uuid: patientId,
            survey_reference: getCurrentTest()?.templateId || '',
          });

          stream.getTracks().forEach(track => track.stop());

          if (response.success && response.text) {
            const detectedLang = response.audioInfo?.detected_language;
            let displayText = response.text;
            let originalText = '';
            let originalLanguage = '';

            if (response.transcription) {
              if (detectedLang === 'es' && response.transcription.spanish_full_text) {
                originalText = response.transcription.spanish_full_text;
                originalLanguage = 'es';
                displayText = response.transcription.english?.full_text || originalText;
              } else if (response.transcription.english) {
                originalText = response.transcription.english.full_text;
                originalLanguage = detectedLang === 'es' ? 'es' : 'en';
                displayText = originalText;
              }
            }

            toast.success(`Audio transcribed successfully`);
            setCommentText(displayText);
            setTranscription({
              text: displayText,
              language: detectedLang,
              originalLanguage: originalLanguage,
              originalText: originalText,
              audioFileUrl: response.fileInfo?.url || '', // Use URL from backend if provided
              s3Key: response.fileInfo?.s3Key || '' // Keep S3 key for permanent reference
            });
          } else {
            toast.error(response.error || 'Failed to transcribe audio');
          }
        } catch (error) {
          console.error('Error processing audio:', error);
          toast.error('Error processing audio');
        } finally {
          setIsProcessingAudio(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTimeLeft(15);

      countdownTimerRef.current = setInterval(() => {
        setRecordingTimeLeft(prev => (prev <= 1 ? 0 : prev - 1));
      }, 1000);

      recordingTimerRef.current = setTimeout(() => {
        if (mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
          setIsRecording(false);
          setRecordingTimeLeft(0);
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
          }
        }
      }, 15000);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingTimeLeft(0);

      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    }
  };

  // Render test selection overview
  const renderTestSelection = () => {
    const completedCount = testsProgress.filter(test => test.completed).length;

    return (
      <Card className="w-full max-w-2xl mx-auto bg-white shadow-md rounded-2xl overflow-hidden border border-gray-100">
        <CardHeader className="bg-[#1a7a4a] p-6">
          <div className="flex items-center justify-center mb-2">
            <CardTitle className="text-2xl font-bold text-white tracking-tight">
              Welcome {patientName}
            </CardTitle>
          </div>
          <p className="text-white/90 text-sm">
            You have {availableTests.length} questionnaire{availableTests.length > 1 ? 's' : ''} to complete
          </p>
          <div className="w-full bg-white/30 rounded-full h-2 mt-4">
            <div
              style={{ width: `${(completedCount / availableTests.length) * 100}%` }}
              className="h-2 rounded-full bg-white transition-all duration-300"
            />
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-4">
          {testsProgress.map((test, index) => (
            <div
              key={test.templateId}
              className={`p-4 rounded-xl border transition-all duration-200 ${test.completed
                ? 'bg-[#e8f5ee] border-[#1a7a4a]/30'
                : index === currentTestIndex
                  ? 'bg-white border-[#1a7a4a] shadow-sm shadow-[#1a7a4a]/10'
                  : 'bg-gray-50 border-gray-100'
                }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">{test.name}</h3>
                  <p className="text-sm text-gray-600">
                    {test.completed
                      ? `Completed - Score: ${test.totalScore}`
                      : index === currentTestIndex
                        ? 'In Progress'
                        : 'Pending'}
                  </p>
                </div>
                <div className="flex items-center">
                  {test.completed ? (
                    <Check className="w-6 h-6 text-[#1a7a4a]" />
                  ) : index === currentTestIndex ? (
                    <div className="w-3 h-3 bg-[#1a7a4a] rounded-full animate-pulse" />
                  ) : (
                    <Circle className="w-6 h-6 text-gray-300" />
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className="pt-4">
            {allTestsCompleted ? (
              <Button
                onClick={() => setCurrentStep('review')}
                className="w-full bg-[#1a7a4a] hover:bg-[#155e39] text-white shadow-md shadow-[#1a7a4a]/20 rounded-xl font-semibold"
              >
                Review All Answers
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentStep('questionnaire')}
                className="w-full bg-[#1a7a4a] hover:bg-[#155e39] text-white shadow-md shadow-[#1a7a4a]/20 rounded-xl font-semibold"
              >
                {currentTestIndex > 0 ? 'Continue Next Test' : 'Start Questionnaires'}
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render current questionnaire
  const renderQuestionnaire = () => {
    const currentTest = getCurrentTest();
    if (!currentTest) return null;

    const progressPercentage = ((currentTestIndex + 1) / availableTests.length) * 100;

    return (
      <Card className="w-full bg-white shadow-sm rounded-2xl overflow-hidden border border-gray-100">
        <CardHeader className="bg-white border-b border-gray-100 p-6">
          <div className="flex items-center mb-6">
            <div className="bg-[#e8f5ee] p-3 rounded-full mr-4 border border-[#1a7a4a]/10">
              <FileText className="text-[#1a7a4a]" size={28} />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
                {currentTest.name}
              </CardTitle>
              <p className="text-sm text-gray-500">
                Test {currentTestIndex + 1} of {availableTests.length}
              </p>
              {currentTest.source_citations && (
                <p className="text-xs text-gray-500 mt-1 italic">
                  Citation: {typeof currentTest.source_citations === 'string' ? currentTest.source_citations : JSON.stringify(currentTest.source_citations)}
                </p>
              )}
            </div>
          </div>

          <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
            <div
              style={{ width: `${progressPercentage}%` }}
              className="h-2 rounded-full bg-[#1a7a4a] transition-all duration-300"
            />
          </div>

          <div className="bg-[#e8f5ee] border border-[#1a7a4a]/20 rounded-xl p-3">
            <p className="text-sm text-[#155e39] font-medium">
              {currentTest.questions.filter(q => q.selected !== null && q.selected !== undefined).length} of {currentTest.questions.length} questions completed
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 p-6">
          {currentTest.questions.map((q, index) => (
            <div key={q.id} className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm transition-all hover:border-[#1a7a4a]/30">
              <p className="font-semibold text-gray-900 mb-4 text-base">
                {index + 1}. {q.question}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {q.options.map((option) => {
                  const isSelected = q.selected === option.value;
                  return (
                    <div
                      key={`${q.id}-${option.value}`}
                      onClick={() => handleResponse(q.id, option.value!)}
                      className={`flex items-center justify-start gap-3 px-4 py-3 rounded-xl cursor-pointer border transition-all duration-200 text-sm font-medium ${isSelected
                        ? "bg-[#e8f5ee] border-[#1a7a4a] text-[#155e39] shadow-inner ring-1 ring-[#1a7a4a]/50"
                        : "bg-white border-gray-200 hover:bg-gray-50 text-gray-600 hover:border-gray-300"
                        } relative ${option.additional_input ? 'pr-8' : ''}`}
                    >
                      {isSelected && q.comment && (
                        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#1a7a4a] rounded-full text-white text-[10px] flex items-center justify-center shadow-sm">
                          <FileText size={10} />
                        </span>
                      )}
                      {!isSelected && option.additional_input && (
                        <span className="absolute top-1.5 right-1.5 text-[#1a7a4a]/50 text-xs">💬</span>
                      )}
                      {isSelected ? (
                        <Check className="text-[#1a7a4a] shrink-0" size={18} />
                      ) : (
                        <Circle className="text-gray-300 shrink-0" size={18} />
                      )}
                      <span className="text-left">{option.label}</span>
                    </div>
                  );
                })}
              </div>

              {q.comment && (
                <div className="mt-4 p-4 bg-[#f8faf9] rounded-xl border border-gray-200/60">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={16} className="text-[#1a7a4a]" />
                    <p className="text-sm font-semibold text-gray-900">Additional Details Added:</p>
                  </div>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{q.comment}</p>
                </div>
              )}
            </div>
          ))}
        </CardContent>

        <div className="flex flex-col md:flex-row justify-between gap-4 p-6 pt-0">
          <Button
            onClick={() => setCurrentStep('selection')}
            variant="outline"
            className="w-full md:w-auto rounded-xl border-gray-200 text-gray-700 hover:bg-gray-50 font-medium"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back to Overview
          </Button>

          <Button
            onClick={completeCurrentTest}
            disabled={!canContinueCurrentTest()}
            className="w-full md:w-auto bg-[#1a7a4a] hover:bg-[#155e39] text-white shadow-md shadow-[#1a7a4a]/20 rounded-xl font-semibold"
          >
            {currentTestIndex >= testsProgress.length - 1 ? 'Complete Test' : 'Next Test'}
            <ChevronRight className="ml-2" size={16} />
          </Button>
        </div>
      </Card>
    );
  };

  // Render review of all tests
  const renderReview = () => {
    const totalCombinedScore = testsProgress.reduce((sum, test) => sum + (test.totalScore || 0), 0);

    return (
      <Card className="max-w-4xl mx-auto bg-white shadow-sm rounded-2xl overflow-hidden border border-gray-100">
        <CardHeader className="bg-white border-b border-gray-100 p-6">
          <CardTitle className="text-2xl font-bold text-gray-900 tracking-tight mb-2">Review All Your Answers</CardTitle>
          <p className="text-gray-500 text-sm">
            All {availableTests.length} questionnaire{availableTests.length > 1 ? 's' : ''} completed
          </p>
        </CardHeader>

        <CardContent className="space-y-8 p-6">
          {testsProgress.map((test, testIndex) => (
            <div key={test.templateId} className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">{test.name}</h3>
                <span className="bg-[#e8f5ee] text-[#155e39] px-3 py-1 rounded-full text-sm font-semibold border border-[#1a7a4a]/10">
                  Score: {test.totalScore}
                </span>
              </div>

              {test.questions.map((q, index) => (
                <div key={q.id} className="bg-white p-4 rounded-xl border border-gray-200">
                  <p className="font-medium text-gray-900 mb-2 text-sm">
                    {index + 1}. {q.question}
                  </p>
                  <p className="text-[#1a7a4a] font-medium text-sm">
                    {q.options.find((opt) => opt.value === q.selected)?.label || "Not answered"}
                  </p>
                  {q.comment && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <p className="text-xs text-gray-800 font-semibold uppercase tracking-wider">Additional Details:</p>
                      <p className="text-sm text-gray-600 mt-1">{q.comment}</p>
                    </div>
                  )}
                </div>
              ))}

              {testIndex < testsProgress.length - 1 && <Separator className="bg-gray-100" />}
            </div>
          ))}

          <div className="bg-[#e8f5ee] p-5 rounded-2xl shadow-sm border border-[#1a7a4a]/10 mt-6">
            <div className="flex justify-between items-center">
              <p className="font-semibold text-gray-900 text-lg">
                Total Combined Score
              </p>
              <p className="font-bold text-[#1a7a4a] text-2xl">
                {totalCombinedScore}
              </p>
            </div>
          </div>
        </CardContent>

        <div className="flex flex-col md:flex-row justify-between gap-4 p-6 pt-0">
          <Button
            onClick={() => setCurrentStep('selection')}
            variant="outline"
            className="w-full md:w-auto rounded-xl border-gray-200 text-gray-700 hover:bg-gray-50 font-medium"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back to Overview
          </Button>

          <Button
            onClick={handleFormSubmit}
            disabled={isLoading}
            className="w-full md:w-auto bg-[#1a7a4a] hover:bg-[#155e39] text-white shadow-md shadow-[#1a7a4a]/20 rounded-xl font-semibold"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Submit All Answers <Check className="ml-2" size={16} />
              </>
            )}
          </Button>
        </div>
      </Card>
    );
  };

  // Render completion screen
  const renderComplete = () => (
    <Card className="max-w-2xl mx-auto text-center bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden">
      <CardHeader className="bg-white border-b border-gray-100 p-8">
        <div className="w-20 h-20 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-[#1a7a4a]/10">
          <Check className="text-[#1a7a4a]" size={40} />
        </div>
        <CardTitle className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
          All Questionnaires Completed!
        </CardTitle>
        <p className="text-gray-500">
          Thank you {patientName} for completing all {availableTests.length} assigned questionnaire{availableTests.length > 1 ? 's' : ''}.
        </p>
      </CardHeader>

      <CardContent className="p-8 space-y-6">
        <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100/60 shadow-sm text-left">
          <p className="text-sm text-gray-700 mb-2">
            <strong className="text-gray-900">Questionnaires Completed:</strong> {availableTests.map(t => t.name).join(', ')}
          </p>
          <p className="text-sm text-gray-600">
            Your responses have been securely saved and will be reviewed by your healthcare provider.
          </p>
        </div>

        <div className="bg-[#e8f5ee]/50 p-5 rounded-2xl border border-[#1a7a4a]/10 shadow-sm text-left">
          <p className="text-sm text-[#155e39]">
            <strong>Next Steps:</strong> Your healthcare provider will review your responses and may contact you to discuss the results or schedule a follow-up appointment.
          </p>
        </div>

        <Button
          onClick={() => navigate(`/patients/${patientId}`)}
          className={cn(
            "w-full mt-6 h-14 text-base font-semibold text-white",
            "bg-[#1a7a4a] hover:bg-[#155e39]",
            "rounded-xl shadow-md shadow-[#1a7a4a]/20",
            "hover:scale-[1.02] transition-transform duration-150 ease-in-out",
            "flex items-center justify-center"
          )}
        >
          Return to Patient Dashboard
        </Button>
      </CardContent>
    </Card>
  );

  // Loading state
  if (isLoadingTests) {
    return (
      <MainLayout hideNavigation={true}>
        <div className="flex-1 min-h-[calc(100vh-4rem)] bg-[#f7f8fa] flex items-center justify-center font-sans">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#1a7a4a] mx-auto mb-4" />
            <p className="text-gray-500 font-medium tracking-tight">Loading assigned questionnaires...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Error state
  if (error) {
    const isNoTestsError = error === 'No tests assigned to this patient';

    return (
      <MainLayout hideNavigation={true}>
        <div className="flex-1 min-h-[calc(100vh-4rem)] bg-[#f7f8fa] flex items-center justify-center font-sans">
          <div className="text-center py-12 max-w-md mx-auto bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            {isNoTestsError ? (
              <>
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-100">
                  <FileText className="text-blue-500" size={32} />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">No Questionnaires Assigned</h2>
                <p className="text-gray-500 mb-6">
                  This patient does not currently have any questionnaires assigned to them.
                </p>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => navigate(`/patients/${patientId}`)}
                    className="w-full bg-[#1a7a4a] hover:bg-[#155e39] text-white rounded-xl"
                  >
                    Go to Patient Dashboard
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-red-500 font-medium mb-6">{error}</p>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="w-full rounded-xl"
                >
                  Try Again
                </Button>
              </>
            )}
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout hideNavigation={true}>
      <div className="min-h-screen bg-[#f7f8fa] p-6 lg:p-10 font-sans">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <div className="mb-8" />

            <div className="text-center">
              <h1 className="text-3xl lg:text-4xl font-extrabold text-[#1a7a4a] tracking-tight">
                Patient Registration
              </h1>
              <p className="text-gray-500 mt-2 text-lg font-medium">
                Step 2 of 3: Questionnaires
              </p>
            </div>

            {/* Progress Bar */}
            <div className="max-w-xs mx-auto mt-6">
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden shadow-inner">
                <div
                  className="h-full bg-[#1a7a4a] rounded-full transition-all duration-700"
                  style={{ width: "66%" }}
                />
              </div>
            </div>
          </div>

          <div className="container mx-auto px-4 py-8">
            {currentStep === 'selection' && renderTestSelection()}
            {currentStep === 'questionnaire' && renderQuestionnaire()}
            {currentStep === 'review' && renderReview()}
            {currentStep === 'complete' && renderComplete()}
          </div>
        </div>
      </div>

      {/* Comment Modal */}
      <Dialog open={!!currentQuestionForComment} onOpenChange={(open) => !open && closeCommentModal()}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col p-4 sm:p-6 w-[95vw] sm:w-full">
          <DialogHeader className="shrink-0">
            <DialogTitle>Additional Details</DialogTitle>
            <DialogDescription className="sr-only">
              Provide additional details or record audio for your response.
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 px-1 pb-1 min-h-0 space-y-4 -mx-1 -mb-1">
            <div className="space-y-2">
              <p className="font-medium text-foreground">Question:</p>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                {currentQuestionForComment?.question}
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-medium text-foreground">Voice Note:</p>
                {isRecording && (
                  <div className="flex items-center gap-2 px-2 py-1 bg-red-50 text-red-600 rounded-md animate-pulse">
                    <div className="w-2 h-2 bg-red-600 rounded-full" />
                    <span className="text-xs font-bold">{recordingTimeLeft}s</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center p-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                {!isRecording && !isProcessingAudio && !transcription && (
                  <Button
                    onClick={startRecording}
                    className="h-16 w-16 rounded-full bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200 flex items-center justify-center transform transition-transform hover:scale-110"
                  >
                    <Mic className="w-8 h-8 text-white" />
                  </Button>
                )}

                {isRecording && (
                  <Button
                    onClick={stopRecording}
                    className="h-16 w-16 rounded-full bg-gray-900 animate-pulse hover:bg-black shadow-lg flex items-center justify-center"
                  >
                    <StopCircle className="w-8 h-8 text-white" />
                  </Button>
                )}

                {isProcessingAudio && (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-green-500 animate-spin" />
                    <p className="text-sm font-medium text-gray-500">Processing audio...</p>
                  </div>
                )}

                {!isRecording && !isProcessingAudio && transcription && (
                  <div className="w-full space-y-3">
                    <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-100">
                      <div className="flex items-center gap-2 text-green-700">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-semibold">Audio Captured</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setTranscription(null); setCommentText(''); }}
                        className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Re-record
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-medium text-foreground">Written Note:</p>
                {transcription?.language && (
                  <span className="text-xs text-gray-500">
                    Detected: {transcription.language.toUpperCase()}
                  </span>
                )}
              </div>

              <Textarea
                placeholder="Please provide more details about your response..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                rows={5}
                disabled={isProcessingAudio || isRecording}
                className="resize-none mt-2 outline-none focus-visible:ring-1 focus-visible:ring-green-500 border-gray-200"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 shrink-0 sm:pt-6">
            <Button
              variant="outline"
              onClick={closeCommentModal}
              disabled={isProcessingAudio || isRecording}
            >
              {isRecording ? 'Stop Recording First' : 'Cancel'}
            </Button>
            <Button
              onClick={saveComment}
              disabled={isProcessingAudio || isRecording}
              className="bg-gray-900 text-white hover:bg-gray-800"
            >
              {isProcessingAudio ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                'Save Details'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Welcome Modal */}
      <Dialog open={showWelcomeModal} onOpenChange={setShowWelcomeModal}>
        <DialogContent className="sm:max-w-[500px] p-6 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1a7a4a] text-center">
              Welcome {patientName || 'Patient'}!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 text-center">
            <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-2 shadow-sm border border-[#1a7a4a]/10">
              <FileText className="text-[#1a7a4a] w-8 h-8" />
            </div>
            <p className="text-gray-600 font-medium text-base">
              We have prepared some health questionnaires for you to complete. 
              Your responses will help your care team monitor your well-being.
            </p>
            <div className="bg-gray-50 border border-gray-100 p-4 rounded-xl mt-4">
              <p className="text-sm text-gray-700">
                Please answer all questions honestly. If you need a break, you can safely close this page and return later to continue.
              </p>
            </div>
          </div>
          <div className="flex justify-center pt-2">
            <Button 
              onClick={() => setShowWelcomeModal(false)}
              className="w-full sm:w-auto bg-[#1a7a4a] hover:bg-[#155e39] text-white rounded-xl h-12 px-8 font-semibold shadow-md shadow-[#1a7a4a]/20"
            >
              Get Started <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Questionnaire;